import { Fragment, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { isNotificationsEnabled } from '@/config/env';
import { ROUTES } from '@/config/routes';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AppIcon } from '@/shared/components/ui/AppIcon/AppIcon';
import type { AppIconName } from '@/shared/components/ui/AppIcon/AppIcon';
import { useModalDialog } from '@/shared/hooks/useModalDialog';
import { DEFAULT_NAV_ITEMS } from './navItems';

export interface SidebarNavItem {
  readonly label: string;
  readonly path: string;
  readonly icon: AppIconName;
  /** Heading of the group this item starts or continues; a new value starts a new group. */
  readonly section?: string;
  /** Rendered by the `renderProjects` slot (a tree) instead of as a plain link. */
  readonly expandable?: boolean;
}

const MOBILE_QUERY = '(max-width: 900px)';
const COLLAPSED_KEY = 'conbent.sidebar.collapsed';
function subscribeMobile(onChange: () => void): () => void {
  const media = window.matchMedia(MOBILE_QUERY);
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

export interface SidebarProjectSlotProps {
  /** The nav item's label, so the slot reads "Models" once the workspace has "Projects". */
  readonly label: string;
  readonly compact: boolean;
  readonly onNavigate: () => void;
  readonly onExpandSidebar: () => void;
}

export interface SidebarProps {
  readonly renderProjects?: (props: SidebarProjectSlotProps) => ReactNode;
  /** Supplied by the composition root, which knows which features Core serves. */
  readonly navItems?: readonly SidebarNavItem[];
}

export function Sidebar({
  renderProjects,
  navItems = DEFAULT_NAV_ITEMS,
}: SidebarProps = {}): React.JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, signOut } = useAuth();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const mobile = useSyncExternalStore(
    subscribeMobile,
    () => window.matchMedia(MOBILE_QUERY).matches,
    () => false
  );
  const dialogRef = useModalDialog(mobile && drawerOpen);
  const accountRef = useRef<HTMLDivElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const drawerId = useId();

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch {
      /* Storage can be disabled. */
    }
  }, [collapsed]);

  useEffect(() => {
    setDrawerOpen(false);
    setMenuOpen(false);
  }, [pathname, mobile]);

  useEffect(() => {
    if (!menuOpen) return;
    const outside = (event: PointerEvent): void => {
      if (!accountRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setMenuOpen(false);
      accountButtonRef.current?.focus();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [menuOpen]);

  const close = (): void => {
    setDrawerOpen(false);
    setMenuOpen(false);
  };
  const compact = !mobile && collapsed;
  const displayName = user === null ? '' : `${user.givenName} ${user.familyName}`.trim() || user.username;
  const initials =
    user === null
      ? ''
      : `${user.givenName[0] ?? user.username[0] ?? '?'}${user.familyName[0] ?? ''}`.toUpperCase();
  const navigation = (
    <nav className={`app-sidebar${compact ? ' app-sidebar--collapsed' : ''}`} aria-label="Main navigation">
      <div className="app-sidebar__top">
        <img
          className="app-sidebar__brand"
          src="/conbent-logo-line.svg"
          alt="Conbent"
          width={148}
          height={40}
        />
        <button
          type="button"
          className="icon-button"
          aria-label={mobile ? 'Close navigation' : collapsed ? 'Expand navigation' : 'Collapse navigation'}
          aria-expanded={mobile ? drawerOpen : !collapsed}
          onClick={() => {
            if (mobile) close();
            else {
              setCollapsed((value) => !value);
              setMenuOpen(false);
            }
          }}
        >
          {mobile ? (
            <AppIcon name="close" size={18} />
          ) : (
            <span style={{ transform: collapsed ? undefined : 'rotate(180deg)', display: 'flex' }}>
              <AppIcon name="chevronRight" size={18} />
            </span>
          )}
        </button>
      </div>
      <div className="app-sidebar__nav">
        {navItems.map((item, index) => {
          const previous = navItems[index - 1];
          const heading =
            item.section !== undefined && item.section !== previous?.section ? item.section : null;

          return (
          <Fragment key={item.path}>
            {heading === null ? null : (
              <span className="app-sidebar__section-label" aria-hidden="true">
                {heading}
              </span>
            )}
        {(item.path === ROUTES.projects || item.expandable === true) && renderProjects !== undefined ? (
          <div>
            {renderProjects({
              label: item.label,
              compact,
              onNavigate: close,
              onExpandSidebar: () => { setCollapsed(false); setMenuOpen(false); },
            })}
          </div>
        ) : (
          <NavLink
            to={item.path}
            onClick={close}
            title={compact ? item.label : undefined}
            className={({ isActive }) => `app-sidebar__item${isActive ? ' is-active' : ''}`}
            aria-label={item.label}
          >
            <span className="app-sidebar__icon">
              <AppIcon name={item.icon} size={20} />
            </span>
            <span className="app-sidebar__label">{item.label}</span>
          </NavLink>
        )}
          </Fragment>
          );
        })}
      </div>
      {user === null ? null : (
        <div className="app-sidebar__account" ref={accountRef}>
          {menuOpen ? (
            <div className="app-sidebar__account-menu" id={menuId} data-testid="account-menu">
              <NavLink to={ROUTES.profile} data-testid="account-profile" onClick={close}>
                Profile
              </NavLink>
              {isNotificationsEnabled ? (
                <NavLink to={ROUTES.notificationSettings} data-testid="account-notifications" onClick={close}>
                  Notifications
                </NavLink>
              ) : null}
              <button
                type="button"
                data-testid="sign-out"
                onClick={() => {
                  close();
                  void signOut().then(() => navigate(ROUTES.login, { replace: true }));
                }}
              >
                Sign out
              </button>
            </div>
          ) : null}
          <button
            type="button"
            ref={accountButtonRef}
            className="app-sidebar__account-button"
            data-testid="account-menu-button"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            aria-label={`Account menu for ${displayName}`}
            onClick={() => setMenuOpen((value) => !value)}
          >
            <span className="app-sidebar__avatar">{initials}</span>
            <span className="app-sidebar__account-details">
              <strong>{displayName}</strong>
              <small>{user.organization}</small>
            </span>
          </button>
        </div>
      )}
    </nav>
  );

  return (
    <div className="navigation-slot">
      <div className="mobile-nav-bar">
        <button
          type="button"
          className="nav-menu-button"
          aria-label="Open navigation"
          aria-expanded={drawerOpen}
          aria-controls={drawerId}
          onClick={() => setDrawerOpen(true)}
        >
          <AppIcon name="menu" size={18} /> Menu
        </button>
        <img src="/conbent-logo-line.svg" alt="Conbent" width={124} height={34} />
      </div>
      {!mobile ? (
        navigation
      ) : (
        <dialog
          ref={dialogRef}
          id={drawerId}
          className="navigation-drawer"
          aria-label="Navigation"
          onCancel={(event) => {
            event.preventDefault();
            close();
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          {drawerOpen ? navigation : null}
        </dialog>
      )}
    </div>
  );
}
